/**
 * Estilos do dashboard configuravel (Bloco VG).
 *
 * Grid responsivo CSS para acomodar widgets de 3 tamanhos:
 *   S = 1 coluna  (~280px min)
 *   M = 2 colunas (~580px min)
 *   L = full row (todas as colunas)
 *
 * Em modo edicao, cards ganham handles + outline; em leitura ficam limpos.
 */
import styled, { css } from "styled-components";
import { BP_MOBILE } from "../../styles/canonical/breakpoints";


export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  align-items: stretch;

  /* Mobile: 1 coluna sempre (widgets full-width, sem scroll lateral). */
  @media (max-width: ${BP_MOBILE}px) {
    grid-template-columns: 1fr;
  }
`;


export const WidgetSlot = styled.div`
  ${({ $tamanho }) => $tamanho === "L" && css`
    grid-column: 1 / -1;
  `}
  ${({ $tamanho }) => $tamanho === "M" && css`
    grid-column: span 2;
    @media (max-width: ${BP_MOBILE}px) {
      grid-column: span 1;
    }
  `}
  /* S = default 1 col */
  display: flex;
  min-width: 0;
`;


/**
 * UI.6 (Bloco UI, auditoria 2026-05-23): polish visual nos widgets.
 * Mais arredondado (radius 12px), padding mais respirado, tipografia
 * mais limpa (sem uppercase agressivo no titulo).
 */
export const Card = styled.article`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
  position: relative;
  transition: border-color 0.15s, box-shadow 0.15s;

  ${({ $editando }) => $editando && css`
    border-color: var(--accent);
    box-shadow: 0 0 0 2px var(--accent-ring);
  `}

  ${({ $dragging }) => $dragging && css`
    opacity: 0.6;
    cursor: grabbing;
  `}
`;


export const CardHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 20px;
`;


/**
 * UI.6: titulo do card mais discreto — caps simples, sem letterspacing
 * agressivo. Inspirado nas referencias (titulo eh contexto, valor eh estrela).
 */
export const CardTitle = styled.h3`
  margin: 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary};
  text-transform: none;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;


export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.textMuted};
`;


export const DragHandle = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  border-radius: 4px;
  color: inherit;
  cursor: grab;
  display: inline-flex;
  align-items: center;

  &:hover { background: ${({ theme }) => theme.surfaceAlt}; }
  &:active { cursor: grabbing; }
`;


export const RemoveButton = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  border-radius: 4px;
  color: var(--danger-text);
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  &:hover { background: var(--danger-bg); }
`;


export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  font-size: 0.92rem;
`;


/**
 * UI.6: KPI dentro dos widgets — valor maior (1.85rem) e font-weight 800
 * para impacto visual, alinhado com referencias. Cor primaria do tema
 * (preto/branco conforme tema).
 */
export const KpiValor = styled.div`
  font-size: 1.85rem;
  font-weight: 800;
  color: ${({ theme }) => theme.textPrimary};
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
`;


export const KpiSub = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textTertiary || theme.textMuted};
  line-height: 1.3;
`;


export const CardFooter = styled.footer`
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;


export const LinkLight = styled.a`
  font-size: 0.78rem;
  color: var(--accent);
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;


export const Skeleton = styled.div`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.surfaceAlt} 0%,
    ${({ theme }) => theme.border} 50%,
    ${({ theme }) => theme.surfaceAlt} 100%
  );
  background-size: 200% 100%;
  animation: vg-skel 1.4s ease-in-out infinite;
  border-radius: 4px;
  height: ${({ $h }) => $h || "1.2em"};
  width: ${({ $w }) => $w || "100%"};

  @keyframes vg-skel {
    0%   { background-position: 100% 50%; }
    100% { background-position: -100% 50%; }
  }
`;


export const ErroInline = styled.div`
  color: var(--danger-text);
  font-size: 0.78rem;
  padding: 6px 8px;
  background: var(--danger-bg);
  border-radius: 4px;
`;


export const VazioMsg = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: ${({ theme }) => theme.textMuted};
  font-size: 0.9rem;
  background: ${({ theme }) => theme.surfaceAlt};
  border-radius: 8px;
  border: 1px dashed ${({ theme }) => theme.border};
`;
